import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useSideDetail from '#/hooks/useSideDetail';
import { createAsyncFilterLabelCache } from '#/modules/commonComponents/table/asyncFilterLabelCache';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import SalesOrderSideMsg from '#/modules/crmMgmt/salesOrder/components/SalesOrderSideMsg';
import { buildSalesOrderColumns } from '#/modules/crmMgmt/salesOrder/data/salesOrderColumns';
import { getSalesOrderApiErrorMessage } from '#/modules/crmMgmt/salesOrder/helper/apiError';
import {
  applyPlannedShipDateRangeToFilters,
} from '#/modules/crmMgmt/salesOrder/helper/salesOrderDateListFilter';
import { canEditSalesOrderDraft } from '#/modules/crmMgmt/salesOrder/helper/salesOrderStatus';
import useSalesOrderList from '#/modules/crmMgmt/salesOrder/hooks/useSalesOrderList';
import {
  hydrateCustomerFilterLabels,
} from '#/modules/crmMgmt/customer/helper/searchCustomerFilterOptions';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';

const SALES_ORDER_SORT_FIELD_LABELS = {
  ...DEFAULT_LIST_SORT_FIELD_LABELS,
  salesOrderNo: '銷貨單號',
  status: '狀態',
  plannedShipDate: '預交日',
  updatedAt: '修改日',
};

const SalesOrderListPage = () => {
  const listRef = useRef(null);
  const { displayData, setSelectedData } = useSideDetail();
  const canCreate = useCan(PERM.SALES_ORDER_CREATE);
  const canUpdate = useCan(PERM.SALES_ORDER_UPDATE);

  const [customerLabelCache] = useState(createAsyncFilterLabelCache);
  const [labelCacheVersion, setLabelCacheVersion] = useState(0);

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
    setListQuery,
  } = useSalesOrderList();

  useEffect(() => {
    let cancelled = false;

    hydrateCustomerFilterLabels(listQuery.filters?.customerId, customerLabelCache)
      .then((didHydrate) => {
        if (!cancelled && didHydrate) {
          setLabelCacheVersion((version) => version + 1);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    listQuery.filters?.customerId,
    customerLabelCache,
  ]);

  // 起迄須同一次寫入 URL；連續兩次 setFilter 會以舊 listQuery 為底，後者蓋掉前者
  const setPlannedShipDateRangeFilter = useCallback((range) => {
    setListQuery((prev) => ({
      ...prev,
      page: 1,
      filters: applyPlannedShipDateRangeToFilters(prev.filters, range),
    }));
  }, [setListQuery]);

  const handleFilterChange = useCallback((key, value) => {
    if (key === 'plannedShipDateFrom' && value === undefined) {
      setPlannedShipDateRangeFilter(undefined);
      return;
    }
    setFilter(key, value);
  }, [setFilter, setPlannedShipDateRangeFilter]);

  const columns = useMemo(
    () => {
      void labelCacheVersion;
      return buildSalesOrderColumns({
        filters: listQuery.filters || {},
        onFilterChange: handleFilterChange,
        onPlannedShipDateRangeChange: setPlannedShipDateRangeFilter,
        customerLabelCache,
        canShowEdit: (row) => canUpdate && canEditSalesOrderDraft(row.status),
      });
    },
    [
      listQuery.filters,
      handleFilterChange,
      setPlannedShipDateRangeFilter,
      customerLabelCache,
      labelCacheVersion,
      canUpdate,
    ],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { filters: listQuery.filters }),
    [columns, listQuery.filters],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowClick = useCallback((row, isSelected) => {
    if (isSelected) {
      setSelectedData(row.original);
      return;
    }
    setSelectedData(null);
  }, [setSelectedData]);

  const headerAction = canCreate ? (
    <ListAddButton to={ROUTES.salesOrderCreate} />
  ) : null;

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <h1 className="page_title">
            <span className="border-l-5 border-primary-400 pl-2.5">銷貨單列表</span>
          </h1>
        </div>
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              ref={listRef}
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={
                isError
                  ? getSalesOrderApiErrorMessage(error, '載入列表失敗')
                  : undefined
              }
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋銷貨單號、備註或事由"
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              sortFieldLabels={SALES_ORDER_SORT_FIELD_LABELS}
              onFilterChange={handleFilterChange}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
              onSortingChange={handleSortingChange}
              headerAction={headerAction}
            />
          </div>

          <SalesOrderSideMsg displayData={displayData} />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default SalesOrderListPage;
